import { AxiosHttpClient } from 'api/axios';
import { useRepruaIndexApi, useRepruaPostApi, useRepruaPatchApi, useRepruaDeleteApi } from 'api/shared_api_hooks';
import { ID, PagingResponse } from 'entities';
import { Usage, UsageForm } from 'entities/usage';
import { ApiSet, BaseResponse, Form, IndexApiSet } from 'rac';
import { useEffectSkipFirst } from 'utils/hooks';

export type UsagesResponse = PagingResponse & {
  usages: Usage[];
};

/**
 * 機器の利用一覧
 */
export type UsagesIndexApiSet = IndexApiSet<UsagesResponse> & { execute: () => void };
export function useFetchInstrumentUsagesApi(instrumentId?: ID): UsagesIndexApiSet {
  const api = useRepruaIndexApi<UsagesResponse, unknown>(new AxiosHttpClient('user'), {
    initialResponse: { usages: [], totalCount: 0 },
  });

  const execute = () => {
    const path = `instruments/${instrumentId}/usages`;
    api.execute(path);
  };

  useEffectSkipFirst(() => {
    execute();
  }, [api.pageSet.page, api.pageSet.perPage]);

  return { ...api, execute };
}

export type UsageResponse = BaseResponse & {
  usage: Usage;
};

/**
 * 機器の利用登録
 */
export function usePostInstrumentUsageApi(): ApiSet<UsageResponse> & {
  execute: (instrumentId: ID, form: Form<UsageForm>) => void;
} {
  const api = useRepruaPostApi<UsageResponse, UsageForm>(new AxiosHttpClient('user'), {
    initialResponse: { usage: {} },
  });

  const execute = (instrumentId: ID, form: Form<UsageForm>) => {
    const path = `instruments/${instrumentId}/usages`;
    api.execute(path, form);
  };

  return { ...api, execute };
}

/**
 * 利用の更新
 */
export function usePatchUsageApi(): ApiSet<UsageResponse> & { execute: (form: UsageForm) => void } {
  const api = useRepruaPatchApi<UsageResponse, UsageForm>(new AxiosHttpClient('user'), {
    initialResponse: { usage: {} },
  });

  const execute = (form: UsageForm) => {
    const path = `usages/${form.id}`;
    api.execute(path, {
      startedAt: form.startedAt,
      endedAt: form.endedAt,
      allDay: !!form.allDay,
      content: form.content,
    });
  };

  return { ...api, execute };
}

/**
 * 利用の削除
 */
export function useDeleteUsageApi(): ApiSet<UsageResponse> & { execute: (id: ID) => void } {
  const api = useRepruaDeleteApi<UsageResponse>(new AxiosHttpClient('user'), {
    initialResponse: { usage: {} },
  });

  const execute = (id: ID) => {
    const path = `usages/${id}`;
    api.execute(path);
  };

  return { ...api, execute };
}
